import {ComponentType, CSSProperties, ReactNode} from 'react'
import styles from './SegmentedSelector.module.css'

export interface SegmentedOption<T extends string> {
    value: T
    label: ReactNode
    icon?: ComponentType<{size?: number; className?: string}>
    disabled?: boolean
}

interface SegmentedSelectorProps<T extends string> {
    options: SegmentedOption<T>[]
    /** `null` renders with no segment selected. */
    value: T | null
    onChange: (value: T) => void
    disabled?: boolean
    iconSize?: number
    ariaLabelledBy?: string
    className?: string
    /** Render icons only; the label is kept for accessibility (visually hidden + button title). */
    iconOnly?: boolean
    /** When false, suppress the hover `title` tooltip on icon-only options (label is still kept for accessibility). */
    showTitle?: boolean
    /** Flags the control as invalid (e.g. a required selection left empty) — red border + aria-invalid. */
    invalid?: boolean
}

const SegmentedSelector = <T extends string>({
    options,
    value,
    onChange,
    disabled = false,
    iconSize = 16,
    ariaLabelledBy,
    className,
    iconOnly = false,
    showTitle = true,
    invalid = false
}: SegmentedSelectorProps<T>) => {
    /* The selected fill is a single absolutely positioned element that slides between
       segments, instead of a background on the selected button — that is what makes the
       change animate rather than jump. Segments are equal-width grid columns
       (grid-auto-columns: 1fr), so the index alone positions it; no DOM measuring. */
    const selectedIndex = options.findIndex((option) => option.value === value)

    return (
        <div
            className={`${styles.segmented}${invalid ? ` ${styles.invalid}` : ''}${className ? ` ${className}` : ''}`}
            role="radiogroup"
            aria-labelledby={ariaLabelledBy}
            aria-invalid={invalid || undefined}
            data-disabled={disabled || undefined}
            style={{'--seg-count': options.length, '--seg-index': selectedIndex} as CSSProperties}
        >
            {/* Rendered only once something is selected: mounting it in place keeps an
                initial selection from sliding in from the first segment. */}
            {selectedIndex >= 0 && <div className={styles.indicator} data-segmented-indicator aria-hidden="true"/>}
            {options.map((option) => {
                const selected = option.value === value
                const Icon = option.icon
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={disabled || option.disabled}
                        title={showTitle && iconOnly && typeof option.label === 'string' ? option.label : undefined}
                        className={`${styles.segment}${selected ? ` ${styles.segmentSelected}` : ''}`}
                        onClick={() => onChange(option.value)}
                    >
                        {Icon && <Icon size={iconSize} className={styles.icon}/>}
                        <span className={iconOnly ? 'visually-hidden' : undefined}>{option.label}</span>
                    </button>
                )
            })}
        </div>
    )
}

export default SegmentedSelector
